import { useState } from "react";
import { ChevronLeft, ChevronRight, SkipForward } from "lucide-react";

// Stable-slot paging: pages are computed over the full snapshot (incl. locally-finished items),
// so finishing rows never shifts the page under the cursor. Finished rows are simply hidden.
export function usePaged(items, isDone, size = 50) {
  const [page, setPage] = useState(1);
  const pages = Math.max(1, Math.ceil(items.length / size));
  const p = Math.min(Math.max(1, page), pages);
  const slice = items.slice((p - 1) * size, p * size);
  const visible = slice.filter((x) => !isDone(x));
  const remaining = items.filter((x) => !isDone(x)).length;
  const jumpUnfinished = () => {
    const i = items.findIndex((x) => !isDone(x));
    setPage(i < 0 ? 1 : Math.floor(i / size) + 1);
  };
  return { page: p, setPage, pages, visible, slotCount: slice.length, remaining, jumpUnfinished, reset: () => setPage(1) };
}

export const Pager = ({ pg, testid = "pager" }) => (
  <div className="flex items-center gap-2 text-xs text-slate-600" data-testid={testid}>
    <button data-testid={`${testid}-prev`} onClick={() => pg.setPage(pg.page - 1)} disabled={pg.page <= 1}
      className="inline-flex items-center rounded-lg border border-slate-300 bg-white p-1.5 hover:border-blue-500 disabled:opacity-40 disabled:hover:border-slate-300"><ChevronLeft className="h-3.5 w-3.5" /></button>
    <span data-testid={`${testid}-label`} className="font-semibold tabular-nums">Page {pg.page} of {pg.pages}</span>
    <button data-testid={`${testid}-next`} onClick={() => pg.setPage(pg.page + 1)} disabled={pg.page >= pg.pages}
      className="inline-flex items-center rounded-lg border border-slate-300 bg-white p-1.5 hover:border-blue-500 disabled:opacity-40 disabled:hover:border-slate-300"><ChevronRight className="h-3.5 w-3.5" /></button>
    <span className="text-slate-400 tabular-nums">{pg.visible.length} of {pg.slotCount} left on this page</span>
    <button data-testid={`${testid}-jump`} onClick={pg.jumpUnfinished} disabled={pg.remaining === 0}
      className="ml-1 inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 font-semibold text-blue-700 hover:border-blue-500 disabled:opacity-40">
      <SkipForward className="h-3.5 w-3.5" /> Jump to first unfinished</button>
  </div>
);

export const ProgressBar = ({ done, total, label = "done", testid = "progress" }) => {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div className="flex items-center gap-3 min-w-[220px]" data-testid={testid}>
      <div className="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs tabular-nums text-slate-600 whitespace-nowrap" data-testid={`${testid}-label`}>
        <b className="text-slate-800">{done}</b> / {total} {label} · {pct}%
      </span>
    </div>
  );
};

// Daily goal ring: fills with today's tally; click the number to set a new target.
export const GoalRing = ({ value, goal, onSave, testid = "goal-ring" }) => {
  const [editing, setEditing] = useState(false);
  const pct = goal > 0 ? Math.min(1, value / goal) : 0;
  const r = 15, c = 2 * Math.PI * r, hit = goal > 0 && value >= goal;
  return (
    <div className="inline-flex items-center gap-2" data-testid={testid} title="Daily goal — click the target to change it">
      <svg width="38" height="38" viewBox="0 0 38 38" className="-rotate-90 shrink-0">
        <circle cx="19" cy="19" r={r} fill="none" stroke="#e2e8f0" strokeWidth="4" />
        <circle cx="19" cy="19" r={r} fill="none" stroke={hit ? "#059669" : "#f97316"} strokeWidth="4" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct)} className="transition-[stroke-dashoffset] duration-700" data-testid={`${testid}-arc`} />
      </svg>
      <div className="leading-tight">
        <div className={`text-xs font-semibold tabular-nums ${hit ? "text-emerald-700" : "text-slate-800"}`} data-testid={`${testid}-label`}>
          {value} / {editing ? (
            <input autoFocus type="number" min="0" max="5000" defaultValue={goal || 100} data-testid={`${testid}-input`}
              onBlur={(e) => { setEditing(false); onSave(Number(e.target.value)); }}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setEditing(false); }}
              className="w-16 rounded border border-slate-300 px-1 py-0.5 text-xs" />
          ) : (
            <button onClick={() => setEditing(true)} data-testid={`${testid}-edit`} className="underline decoration-dotted hover:text-blue-600">{goal || "set goal"}</button>
          )}
        </div>
        <div className="text-[10px] text-slate-400">{hit ? "daily goal reached" : "today's goal"}</div>
      </div>
    </div>
  );
};

// 7-day strip: one bar per UTC day (oldest → today), height relative to the best day; today is highlighted.
export const WeekStrip = ({ days = [], goal = 0, testid = "week-strip" }) => {
  const max = Math.max(goal || 0, ...days.map((d) => d.total), 1);
  return (
    <div className="inline-flex items-end gap-[3px] h-9" data-testid={testid} title="Last 7 days (UTC): ticks + reviews per day">
      {days.map((d, i) => {
        const isToday = i === days.length - 1; const h = Math.max(2, Math.round((d.total / max) * 32)); const hit = goal > 0 && d.total >= goal;
        return (
          <div key={d.date} className="group relative flex flex-col items-center justify-end h-full w-[9px]" data-testid={`${testid}-day`} data-total={d.total}>
            <div className={`w-full rounded-sm transition-[height] duration-500 ${hit ? "bg-emerald-500" : isToday ? "bg-orange-500" : d.total ? "bg-orange-200" : "bg-slate-200"}`} style={{ height: `${h}px` }} />
            <div className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-white opacity-0 group-hover:opacity-100">{d.date.slice(5)} · {d.total}</div>
          </div>
        );
      })}
    </div>
  );
};
